// Hierarchical planner: decomposes the query into a tree of sub-questions
// using *domain-aware personas* (no hardcoded sub-agent names leaking
// from previous contexts). Pure logic; emits plan events via the engine bus.

import { DEFAULT_CONFIG, type ResearchConfig } from "./config";
import { DOMAIN_PERSONAS, type DomainClassification } from "./domain";
import type { PlanNode, PlanTree, ToolHint } from "./types";
import { pick, randInt, sleep, uid, shortHash } from "./util";

const DIM_TEMPLATES: Array<{ tag: string; q: (topic: string) => string; objective: string; tools: ToolHint[] }> = [
  { tag: "definitions",   q: (t) => `What does "${t}" mean and what are its core sub-problems?`,
    objective: "Establish shared vocabulary and decomposition",
    tools: ["bm25", "dense"] },
  { tag: "approaches",    q: (t) => `What are the main technical approaches addressing ${t}?`,
    objective: "Enumerate competing methods and assumptions",
    tools: ["dense", "graph_rag"] },
  { tag: "evidence",      q: (t) => `What empirical evidence supports or refutes leading methods for ${t}?`,
    objective: "Surface benchmarks, ablations, replications",
    tools: ["dense", "rerank", "self_consistency"] },
  { tag: "limitations",   q: (t) => `What are the known limitations and failure modes of current ${t} solutions?`,
    objective: "Catalog weaknesses and counterexamples",
    tools: ["critic", "multi_hop"] },
  { tag: "frontiers",     q: (t) => `What promising directions or open problems remain for ${t}?`,
    objective: "Identify research frontier and unknowns",
    tools: ["graph_rag", "dense"] },
  { tag: "actors",        q: (t) => `Which labs, authors and institutions lead work on ${t}?`,
    objective: "Map influence network and recent leaders",
    tools: ["graph_rag", "bm25"] },
  { tag: "timeline",      q: (t) => `How has the state of the art on ${t} evolved in the last 24 months?`,
    objective: "Reconstruct chronological progression",
    tools: ["bm25", "rerank"] },
];

export interface BuildPlanArgs {
  query: string;
  domain?: DomainClassification;
  depth?: number;
  branching?: number;
  config?: ResearchConfig;
  signal?: AbortSignal;
  onNode?: (node: PlanNode) => void;
  onAudit?: (entry: { kind: "plan.create" | "plan.expand"; nodeId: string; payload: unknown }) => Promise<void> | void;
}

export async function buildPlanTree(args: BuildPlanArgs): Promise<PlanTree> {
  const cfg = args.config ?? DEFAULT_CONFIG;
  const depth = clampInt(args.depth ?? cfg.planner.defaultDepth, cfg.planner.minDepth, cfg.planner.maxDepth);
  const branching = clampInt(args.branching ?? cfg.planner.branching.default, cfg.planner.branching.min, cfg.planner.branching.max);

  // Build sub-personas dynamically from the classified domain instead of
  // hardcoded names like "Reproducibility evidence" / "Adversarial cases…".
  const personas = args.domain ? DOMAIN_PERSONAS[args.domain.primary] : DOMAIN_PERSONAS.general;
  const subDimGenerators: Array<(parent: string) => string> = personas.map(
    (persona) => (parent: string) => `${persona}: ${parent}`,
  );

  const nodes: Record<string, PlanNode> = {};
  const root: PlanNode = {
    id: uid("n"),
    parentId: null,
    depth: 0,
    question: args.query,
    objective: "Produce a calibrated, citation-grounded dossier",
    successCriteria: "All sub-questions answered with ≥ accepted claims and convergent evidence",
    tools: ["bm25", "dense", "graph_rag", "rerank"],
    status: "pending",
    childIds: [],
  };
  nodes[root.id] = root;
  args.onNode?.(root);
  await args.onAudit?.({
    kind: "plan.create",
    nodeId: root.id,
    payload: {
      question: root.question, depth, branching,
      domain: args.domain ? { primary: args.domain.primary, secondary: args.domain.secondary, conf: args.domain.confidence } : undefined,
    },
  });

  await sleep(rand(cfg.planner.plannerLatencyMs.min, cfg.planner.plannerLatencyMs.max), args.signal);

  // Level 1: pick `branching` distinct dimensions
  const dims = shuffle(DIM_TEMPLATES).slice(0, branching);
  for (const dim of dims) {
    const n: PlanNode = {
      id: uid("n"),
      parentId: root.id,
      depth: 1,
      question: dim.q(args.query),
      objective: dim.objective,
      successCriteria: `Return ≥ 2 supported claims tagged ${dim.tag}`,
      tools: dim.tools,
      status: "pending",
      childIds: [],
    };
    nodes[n.id] = n;
    root.childIds.push(n.id);
    args.onNode?.(n);
    await args.onAudit?.({ kind: "plan.expand", nodeId: n.id, payload: { parent: root.id, dim: dim.tag } });
    await sleep(rand(80, 200), args.signal);

    // Deeper levels: expand a subset to depth using *domain personas*
    let frontier: PlanNode[] = [n];
    for (let d = 2; d <= depth; d++) {
      const nextFrontier: PlanNode[] = [];
      for (const parent of frontier) {
        const nChildren = randInt(1, Math.max(1, Math.min(branching - 1, subDimGenerators.length)));
        const subs = shuffle(subDimGenerators).slice(0, nChildren);
        for (const sub of subs) {
          const child: PlanNode = {
            id: uid("n"),
            parentId: parent.id,
            depth: d,
            question: sub(parent.question.replace(/[?]+$/, "")),
            objective: `Refine ${parent.objective.toLowerCase()} along a domain-specific axis`,
            successCriteria: "Return ≥ 1 supported claim with explicit evidence",
            tools: pick([
              ["dense", "rerank"] as ToolHint[],
              ["bm25", "dense"] as ToolHint[],
              ["graph_rag", "multi_hop"] as ToolHint[],
              ["dense", "self_consistency"] as ToolHint[],
              ["critic", "rerank"] as ToolHint[],
            ]),
            status: "pending",
            childIds: [],
          };
          nodes[child.id] = child;
          parent.childIds.push(child.id);
          args.onNode?.(child);
          nextFrontier.push(child);
        }
      }
      await args.onAudit?.({ kind: "plan.expand", nodeId: n.id, payload: { level: d, expanded: nextFrontier.length } });
      await sleep(rand(60, 160), args.signal);
      frontier = nextFrontier;
    }
  }

  return { rootId: root.id, nodes, maxDepth: depth };
}

// Return only leaves — these are the units sub-agents will work on in parallel.
export function planLeaves(plan: PlanTree): PlanNode[] {
  return Object.values(plan.nodes).filter((n) => n.childIds.length === 0);
}

export async function planSignature(plan: PlanTree): Promise<string> {
  return shortHash({
    depth: plan.maxDepth,
    nodes: Object.values(plan.nodes).map((n) => ({ id: n.id, q: n.question })),
  });
}

function clampInt(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, Math.round(v)));
}
function rand(a: number, b: number) {
  return a + Math.random() * (b - a);
}
function shuffle<T>(a: T[]): T[] {
  const arr = a.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}
